import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tabs,
  Tab,
  useTheme
} from '@mui/material';
import { 
  Upload as UploadIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Label as LabelIcon,
  Transform as TransformIcon
} from '@mui/icons-material';

// CSV-only version - no external libraries needed

function LabelsApp() {
  const theme = useTheme();
  const fileInputRef = useRef(null);
  
  // Tab state
  const [currentTab, setCurrentTab] = useState(0);
  
  // State for the label printer
  const [table, setTable] = useState([]);
  const [fileNameBase, setFileNameBase] = useState("labels");
  const [status, setStatus] = useState({ message: "", type: "info" });
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage] = useState(50);
  
  // Form state
  const [wrapWidth, setWrapWidth] = useState(40);
  const [qrPrefix, setQrPrefix] = useState("QA,*BN");
  const [lineEndings, setLineEndings] = useState("\\n");
  
  // Font converter state
  const [inputZPL, setInputZPL] = useState("");
  const [convertedZPL, setConvertedZPL] = useState("");
  const [converterStatus, setConverterStatus] = useState({ message: "", type: "info" });

  // ZPL Template
  const ZPL_TEMPLATE = `^XA^PON^PQ1^MUd^PW685^MNY^LH0,0^CI13^FWN^LT16
^FO120,24^A0N,30,30^FB420,1,0,L,0^FD{PART}^FS
^FO120,62^GB420,2^FS
^FO120,78^A0N,22,22^FB420,1,0,L,0^FD{DESC1}^FS
^FO120,108^A0N,22,22^FB420,1,0,L,0^FD{DESC2}^FS
^FO555,20^BQ,2,4^FD{QR_FULL}^FS
^FO120,160^A0N,26,26^FB420,1,0,L,0^FD{LOCATION}^FS
^XZ
`;

  // Initialize CSV-only mode
  useEffect(() => {
    setStatus({ message: "CSV-only mode ready. Upload a CSV file with 4 columns: Location, Part #, Description, Bin ID/QR Code", type: "info" });
  }, []);

  const setStatusMessage = (msg, type = "info") => {
    setStatus({ message: msg, type });
  };

  const setConverterStatusMessage = (msg, type = "info") => {
    setConverterStatus({ message: msg, type });
  };

  const sanitize = (v) => {
    return (v == null) ? "" : String(v);
  };

  const wrapTwoLines = (text, width) => {
    text = sanitize(text).trim();
    if (!text) return ["", ""];
    const words = text.split(/\s+/);
    const lines = [""];
    for (const w of words) {
      const cur = lines[lines.length - 1];
      if ((cur.length ? cur.length + 1 : 0) + w.length <= width) {
        lines[lines.length - 1] = cur ? (cur + " " + w) : w;
      } else {
        lines.push(w);
        if (lines.length >= 3) break;
      }
    }
    const line1 = lines[0] || "";
    let line2 = lines[1] || "";
    if (lines.length > 2) {
      if (line2.length > Math.max(0, width - 2)) line2 = line2.slice(0, Math.max(0, width - 2));
      line2 += "..";
    }
    return [line1, line2];
  };

  const buildZPLRow = (row, wrapWidth, qrPrefix) => {
    const location = sanitize(row[0]);
    const part = sanitize(row[1]);
    const desc = sanitize(row[2]);
    const qr = sanitize(row[3]);
    const [d1, d2] = wrapTwoLines(desc, wrapWidth);
    const qrFull = (qrPrefix || "") + qr;
    let zpl = ZPL_TEMPLATE
      .replace("{PART}", part)
      .replace("{DESC1}", d1)
      .replace("{DESC2}", d2)
      .replace("{QR_FULL}", qrFull)
      .replace("{LOCATION}", location);
    return { zpl, computed: { location, part, desc1: d1, desc2: d2, qrFull } };
  };

  const buildAllZPL = (lineEnding) => {
    let out = "";
    for (const r of table) out += buildZPLRow(r, wrapWidth, qrPrefix).zpl;
    // normalize line endings
    if (lineEnding === "\\r\\n") {
      out = out.replace(/\r?\n/g, "\r\n");
    }
    return out;
  };

  // Font conversion functions
  const convertFontSizes = (zplCode) => {
    if (!zplCode.trim()) {
      setConverterStatusMessage("Please enter ZPL code to convert.", "error");
      return "";
    }

    try {
      // Convert small fonts to larger fonts with better scaling
      let converted = zplCode;
      
      // Split by label boundaries and process each label separately
      const labels = converted.split(/(?=\^XA)/);
      const validLabels = labels.filter(label => label.trim() && label.includes('^XA'));
      const labelCount = validLabels.length;
      
      converted = labels.map(label => {
        if (!label.trim()) return label;
        
        let partNumberConverted = false;
        let isHeaderLabel = label.includes('^A0N,28,28'); // Check if this is a header label
        
        // Convert A0N font sizes with special handling for part numbers per label
        return label.replace(/\^A0N,(\d+),(\d+)/g, (match, height, width) => {
          const h = parseInt(height);
          const w = parseInt(width);
          let newHeight, newWidth;
          
          // Scale to improve readability while maintaining layout
          if (h >= 28) {
            // 28,28 fonts are location headers - scale to 36,36
            newHeight = 36;
            newWidth = 36;
          } else if (h >= 22 && !partNumberConverted) {
            // First 22,22 font in this label
            partNumberConverted = true;
            if (isHeaderLabel) {
              // In header labels, first 22,22 is a description - convert to 26,26
              newHeight = 26;
              newWidth = 26;
            } else {
              // In bin labels, first 22,22 is a part number - convert to 40,40
              newHeight = 40;
              newWidth = 40;
            }
          } else if (h >= 22) {
            // Subsequent 22,22 fonts are descriptions - scale up slightly
            newHeight = 26;
            newWidth = 26;
          } else if (h >= 18) {
            // 18,18 fonts are locations - scale up to 28,28
            newHeight = 28;
            newWidth = 28;
          } else {
            // Other small text - scale up moderately
            newHeight = 22;
            newWidth = 22;
          }
          
          return `^A0N,${newHeight},${newWidth}`;
        });
      }).join('');
      
      // Second pass: Convert other A0 font variations (excluding A0N which was handled above)
      converted = converted.replace(/\^A0[^N][^,]*,\s*(\d+),\s*(\d+)/g, (match, height, width) => {
        const h = parseInt(height);
        const w = parseInt(width);
        let newHeight, newWidth;
        
        if (h >= 28) {
          newHeight = 28;
          newWidth = 28;
        } else if (h >= 22) {
          newHeight = 26;
          newWidth = 26;
        } else if (h >= 18) {
          newHeight = 28;
          newWidth = 28;
        } else {
          newHeight = 22;
          newWidth = 22;
        }
        
        return match.replace(/\d+,\s*\d+/, `${newHeight},${newWidth}`);
      });
      
      // Third pass: Adjust field positions to accommodate larger fonts
      converted = converted.replace(/\^FO(\d+),(\d+)/g, (match, x, y) => {
        const newX = Math.max(parseInt(x) - 15, 0);
        const newY = Math.max(parseInt(y) - 8, 0);
        return `^FO${newX},${newY}`;
      });
      
      // Fourth pass: Adjust field box dimensions for larger fonts
      converted = converted.replace(/\^FB(\d+),(\d+),(\d+),([^,]+),(\d+)/g, (match, width, maxLines, lineSpacing, justification, hangingIndent) => {
        const newWidth = Math.max(parseInt(width) + 20, 400);
        return `^FB${newWidth},${maxLines},${lineSpacing},${justification},${hangingIndent}`;
      });
      
      // Fifth pass: Adjust line thickness and position for better visibility
      converted = converted.replace(/\^GB(\d+),(\d+)/g, (match, width, height) => {
        const newHeight = Math.max(parseInt(height) + 1, 3);
        return `^GB${width},${newHeight}`;
      });
      
      // Sixth pass: Move line breaks down slightly to accommodate larger fonts
      converted = converted.replace(/\^FO(\d+),(\d+)\^GB(\d+),(\d+)/g, (match, x, y, width, height) => {
        const newY = parseInt(y) + 5; // Move line down by 5 points
        return `^FO${x},${newY}^GB${width},${height}`;
      });
      
      // Seventh pass: Move QR codes to the right for better spacing
      converted = converted.replace(/\^FO(\d+),(\d+)\^BQ,(\d+),(\d+)/g, (match, x, y, mode, magnification) => {
        const newX = parseInt(x) + 30; // Move QR code right by 30 points
        return `^FO${newX},${y}^BQ,${mode},${magnification}`;
      });

      setConverterStatusMessage(`Font sizes converted successfully! Processed ${labelCount} label${labelCount !== 1 ? 's' : ''}. Location headers set to 36,36, bin part numbers to 40,40, descriptions to 26,26.`, "success");
      return converted;
    } catch (error) {
      setConverterStatusMessage("Error converting font sizes: " + error.message, "error");
      return "";
    }
  };

  const handleConvertFonts = () => {
    const converted = convertFontSizes(inputZPL);
    setConvertedZPL(converted);
  };

  const downloadConvertedZPL = () => {
    if (!convertedZPL.trim()) {
      setConverterStatusMessage("No converted ZPL to download.", "error");
      return;
    }
    
    const blob = new Blob([convertedZPL], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "converted_labels.zpl";
    a.click();
    URL.revokeObjectURL(a.href);
    setConverterStatusMessage("Downloaded converted ZPL file.", "success");
  };

  const printConvertedZPL = () => {
    if (!convertedZPL.trim()) {
      setConverterStatusMessage("No converted ZPL to print.", "error");
      return;
    }

    const w = window.open("", "_blank");
    if (!w) {
      setConverterStatusMessage("Popup blocked. Allow popups for this site.", "error");
      return;
    }
    
    const esc = convertedZPL.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

    w.document.write(
      '<!doctype html><meta charset="utf-8">' +
      '<title>Converted ZPL Output</title>' +
      '<body onload="window.print()" style="margin:0">' +
      '<pre style="white-space:pre-wrap;word-wrap:break-word;font:13px/1.3 Consolas,\'Courier New\',monospace;">' +
      esc +
      '</pre></body>'
    );
    w.document.close();
    setConverterStatusMessage("Opened printable converted ZPL in a new tab.", "success");
  };

  // Calculate pagination
  const startIndex = currentPage * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentData = table.slice(startIndex, endIndex);
  const totalPages = Math.ceil(table.length / rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const generateDownload = () => {
    if (!table.length) {
      setStatusMessage("No data loaded.", "error");
      return;
    }
    const out = buildAllZPL(lineEndings);
    const blob = new Blob([out], { type: "text/plain;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = fileNameBase + "_labels.zpl";
    a.click();
    URL.revokeObjectURL(a.href);
    setStatusMessage(`Generated ${table.length} labels → ${fileNameBase}_labels.zpl`, "success");
  };

  const openPrintable = () => {
    if (!table.length) {
      setStatusMessage("No data loaded.", "error");
      return;
    }
    const out = buildAllZPL(lineEndings);

    const w = window.open("", "_blank");
    if (!w) {
      setStatusMessage("Popup blocked. Allow popups for this site.", "error");
      return;
    }
    const esc = out.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

    w.document.write(
      '<!doctype html><meta charset="utf-8">' +
      '<title>ZPL Output</title>' +
      '<body onload="window.print()" style="margin:0">' +
      '<pre style="white-space:pre-wrap;word-wrap:break-word;font:13px/1.3 Consolas,\'Courier New\',monospace;">' +
      esc +
      '</pre></body>'
    );
    w.document.close();
    setStatusMessage("Opened printable ZPL in a new tab.", "success");
  };

  const readCSV = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target.result;
          const lines = text.split('\n').filter(line => line.trim());
          const data = lines.map(line => {
            // Simple CSV parsing - handles basic comma separation
            const cells = [];
            let current = '';
            let inQuotes = false;
            
            for (let i = 0; i < line.length; i++) {
              const char = line[i];
              if (char === '"') {
                inQuotes = !inQuotes;
              } else if (char === ',' && !inQuotes) {
                cells.push(current.trim());
                current = '';
              } else {
                current += char;
              }
            }
            cells.push(current.trim());
            return cells;
          });
          resolve(data);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  };

  // Excel support removed for memory efficiency

  const handleFileUpload = async () => {
    const file = fileInputRef.current?.files?.[0];
    if (!file) {
      setStatusMessage("Choose a file first.", "error");
      return;
    }

    const baseName = (file.name || "labels").replace(/\.[^.]+$/, "");
    setFileNameBase(baseName);
    const ext = (file.name.split('.').pop() || "").toLowerCase();

    // Only support CSV files
    if (ext !== "csv") {
      setStatusMessage("Only CSV files are supported. Please convert your Excel file to CSV format.", "error");
      return;
    }

    setStatusMessage("Reading CSV file...", "info");
    
    try {
      const rows = await readCSV(file);
      const filteredRows = rows.filter(r => r.length >= 4);
      setTable(filteredRows);
      setCurrentPage(0); // Reset to first page
      setStatusMessage(`Loaded ${filteredRows.length} rows from ${file.name}`, "success");
    } catch (e) {
      console.error(e);
      setStatusMessage("Failed to read CSV file: " + e.message, "error");
    }
  };



  return (
    <Box sx={{ p: 3, height: '100vh', overflow: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <LabelIcon sx={{ fontSize: 32, color: theme.palette.primary.main, mr: 2 }} />
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Bin Label Printer
        </Typography>
      </Box>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
          <Tab 
            label="Label Converter" 
            icon={<TransformIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="CSV Upload" 
            icon={<UploadIcon />} 
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {currentTab === 0 && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Paste your ZPL code with small font in the first box, then click Convert to generate to the larger font version.
          </Typography>

          {converterStatus.message && (
            <Alert 
              severity={converterStatus.type === "error" ? "error" : converterStatus.type === "success" ? "success" : "info"}
              sx={{ mb: 3 }}
            >
              {converterStatus.message}
            </Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Input ZPL (Small Font)" />
                <CardContent>
                  <TextField
                    fullWidth
                    multiline
                    rows={15}
                    value={inputZPL}
                    onChange={(e) => setInputZPL(e.target.value)}
                    placeholder="Paste your ZPL code here..."
                    variant="outlined"
                    sx={{ 
                      '& .MuiInputBase-input': { 
                        fontFamily: 'monospace',
                        fontSize: '0.875rem'
                      }
                    }}
                  />
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      startIcon={<TransformIcon />}
                      onClick={handleConvertFonts}
                      disabled={!inputZPL.trim()}
                    >
                      Convert Fonts
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Converted ZPL (Large Font)" />
                <CardContent>
                  <TextField
                    fullWidth
                    multiline
                    rows={15}
                    value={convertedZPL}
                    onChange={(e) => setConvertedZPL(e.target.value)}
                    placeholder="Converted ZPL will appear here..."
                    variant="outlined"
                    sx={{ 
                      '& .MuiInputBase-input': { 
                        fontFamily: 'monospace',
                        fontSize: '0.875rem'
                      }
                    }}
                  />
                  <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={downloadConvertedZPL}
                      disabled={!convertedZPL.trim()}
                    >
                      Download ZPL
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<PrintIcon />}
                      onClick={printConvertedZPL}
                      disabled={!convertedZPL.trim()}
                    >
                      Print Labels
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      {currentTab === 1 && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Upload a CSV file with 4 columns in this order: Location, Part #, Description, Bin ID/QR Code.
          </Typography>

          {status.message && (
            <Alert 
              severity={status.type === "error" ? "error" : status.type === "success" ? "success" : "info"}
              sx={{ mb: 3 }}
            >
              {status.message}
            </Alert>
          )}

          <Card sx={{ mb: 3 }}>
            <CardHeader title="Configuration" />
            <CardContent>
              <Grid container spacing={3} alignItems="center">
                <Grid item xs={12} sm={6} md={2}>
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      <strong>Data file</strong>
                    </Typography>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      style={{ width: '100%' }}
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="Wrap width (chars)"
                    type="number"
                    value={wrapWidth}
                    onChange={(e) => setWrapWidth(parseInt(e.target.value) || 40)}
                    inputProps={{ min: 20, max: 60 }}
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="QR prefix"
                    value={qrPrefix}
                    onChange={(e) => setQrPrefix(e.target.value)}
                    size="small"
                  />
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Line endings</InputLabel>
                    <Select
                      value={lineEndings}
                      onChange={(e) => setLineEndings(e.target.value)}
                      label="Line endings"
                    >
                      <MenuItem value="\\n">LF (\n)</MenuItem>
                      <MenuItem value="\\r\\n">CRLF (\r\n)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6} md={2}>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      variant="outlined"
                      startIcon={<UploadIcon />}
                      onClick={handleFileUpload}
                      size="small"
                    >
                      Upload Bin Data
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<DownloadIcon />}
                      onClick={generateDownload}
                      size="small"
                      disabled={!table.length}
                    >
                      Download ZPL
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<PrintIcon />}
                      onClick={openPrintable}
                      size="small"
                      disabled={!table.length}
                    >
                      Print Labels
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {table.length > 0 && (
            <Card>
              <CardHeader 
                title={`Bin Data (${table.length} total rows)`}
              />
              <CardContent>
                <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Row</strong></TableCell>
                        <TableCell><strong>Location</strong></TableCell>
                        <TableCell><strong>Part #</strong></TableCell>
                        <TableCell><strong>Description</strong></TableCell>
                        <TableCell><strong>Bin ID/QR Code</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {currentData.map((row, index) => {
                        const actualIndex = startIndex + index;
                        const [location, part, description, qrCode] = row;
                        const [desc1, desc2] = wrapTwoLines(description, wrapWidth);
                        const qrFull = (qrPrefix || "") + qrCode;
                        
                        return (
                          <TableRow key={actualIndex} hover>
                            <TableCell>{actualIndex + 1}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                              {location}
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                              {part}
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                              <Box>
                                <div>{desc1}</div>
                                {desc2 && <div style={{ color: 'text.secondary' }}>{desc2}</div>}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                              {qrFull}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <TablePagination
                  component="div"
                  count={table.length}
                  page={currentPage}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[50]}
                  labelDisplayedRows={({ from, to, count }) => 
                    `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                  }
                />
              </CardContent>
            </Card>
          )}
        </>
      )}

    </Box>
  );
}

export default LabelsApp;
